import React, { useState, useEffect } from "react"
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Avatar,
  Divider,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from "@mui/material"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import FullCalendar from "@fullcalendar/react"
import esLocale from "@fullcalendar/core/locales/es"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import dayjs from "dayjs"
import { useMutation, api, showSuccess } from "../../api"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
  },
})

// Función para obtener color según estado
const getEstadoColor = (estado) => {
  switch (estado) {
    case "Completado":
    case "COMPLETADO":
    case "Completado (Horas extras)":
      return "#4CAF50"
    case "En trabajo":
    case "EN_TRABAJO":
      return "#2196F3"
    case "Pendiente":
    case "PENDIENTE":
    case "NO_MARCADO":
      return "#FF9800"
    default:
      return "#9E9E9E"
  }
}

// Formatear tiempo trabajado
const formatTiempoTrabajado = (tiempo) => {
  if (!tiempo) return "0h 00m 00s"
  const [horas, minutos, segundos] = tiempo.split(":")
  return `${parseInt(horas)}h ${minutos}m ${segundos}s`
}

// Renderizado de eventos
const renderEventContent = (eventInfo) => {
  const event = eventInfo.event
  const startTime = event.start ? dayjs(event.start).format("HH:mm") : ""
  const usuario = event.title || ""

  return (
    <div
      style={{
        padding: "2px 4px",
        fontSize: "0.7rem",
        lineHeight: "1.1",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.65rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {startTime} - {usuario}
      </div>
      <div
        style={{
          fontSize: "0.6rem",
          opacity: 0.8,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {event.extendedProps?.estado_visual || event.extendedProps?.estado || "Pendiente"}
      </div>
    </div>
  )
}

const ConsultaDeAsistencia = () => {
  const [eventos, setEventos] = useState([])
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null)
  const [filtros, setFiltros] = useState({
    emcodemp: "",
    mes: dayjs().format("YYYY-MM"),
  })
  const [estadisticas, setEstadisticas] = useState({
    totalEventos: 0,
    completados: 0,
    enTrabajo: 0,
    pendientes: 0,
  })
  const [esAdministrador, setEsAdministrador] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const handleToggle = () => setExpanded((prev) => !prev)

  // 1. Mutation para obtener eventos del calendario (POST)
  const {
    mutate: fetchEventosCalendario,
    data: eventosData,
    isPending: eventosLoading,
    error: eventosError,
  } = useMutation({
    fn: async (filtros) => {
      const response = await api.post("/ConsultaDeAsistencia/getAllEventsForCalendar", filtros)
      return response
    },
    showError: "modal",
    showSuccess: "silent",
    onSuccess: (data) => {
      if (data?.eventos) {
        setEventos(data.eventos)
        calcularEstadisticas(data.eventos)
        if (data?.es_administrador) {
          setEsAdministrador(true)
        }
      }
    },
  })

  // 2. Mutation para obtener detalles de un evento
  const {
    mutate: fetchDetallesAsistencia,
    data: detallesAsistencia,
    isPending: detallesLoading,
    error: detallesError,
  } = useMutation({
    fn: async (queryParams) => {
      const response = await api.post("/ConsultaDeAsistencia/getAsistenciasByEvento", queryParams)
      return response
    },
    showError: "modal",
    showSuccess: "silent",
  })

  // Cargar eventos iniciales
  useEffect(() => {
    fetchEventosCalendario(filtros)
  }, [])

  // Calcular estadísticas
  const calcularEstadisticas = (eventosLista) => {
    const eventosFiltrados = fechaSeleccionada
      ? eventosLista.filter((e) => {
          const fechaEvento = dayjs(e.start).format("YYYY-MM-DD")
          return fechaEvento === fechaSeleccionada
        })
      : eventosLista

    const completados = eventosFiltrados.filter(
      (e) =>
        e.extendedProps?.estado_visual === "Completado" ||
        e.extendedProps?.estado_visual === "Completado (Horas extras)" ||
        e.extendedProps?.estado === "COMPLETADO",
    ).length

    const enTrabajo = eventosFiltrados.filter(
      (e) => e.extendedProps?.estado_visual === "En trabajo" || e.extendedProps?.estado === "EN_TRABAJO",
    ).length

    const pendientes = eventosFiltrados.filter(
      (e) =>
        e.extendedProps?.estado_visual === "Pendiente" ||
        e.extendedProps?.estado === "NO_MARCADO" ||
        e.extendedProps?.estado === "PENDIENTE",
    ).length

    setEstadisticas({
      totalEventos: eventosFiltrados.length,
      completados,
      enTrabajo,
      pendientes,
    })
  }

  // Manejadores de eventos
  const handleEventClick = (info) => {
    const queryParams = info.event.extendedProps?.queryParams

    if (queryParams) {
      setSelectedEvent(info.event)
      setShowDetailsModal(true)
      fetchDetallesAsistencia(queryParams)
    }
  }

  const handleDateClick = (info) => {
    const fecha = info.dateStr
    setFechaSeleccionada(fecha)
    calcularEstadisticas(eventos)
  }

  const handleFiltroChange = (e) => {
    const { name, value } = e.target
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const aplicarFiltros = () => {
    fetchEventosCalendario(filtros)
  }

  const resetFiltros = () => {
    const nuevosFiltros = {
      emcodemp: "",
      mes: dayjs().format("YYYY-MM"),
    }
    setFiltros(nuevosFiltros)
    setFechaSeleccionada(null)
    fetchEventosCalendario(nuevosFiltros)
  }

  const resetFiltroFecha = () => {
    setFechaSeleccionada(null)
    calcularEstadisticas(eventos)
  }

  // Generar opciones de meses
  const generarOpcionesMeses = () => {
    const meses = []
    const hoy = dayjs()

    // 6 meses anteriores
    for (let i = 6; i >= 1; i--) {
      const mes = hoy.subtract(i, "month")
      meses.push({
        value: mes.format("YYYY-MM"),
        label: mes.format("MMMM YYYY"),
      })
    }

    // Mes actual
    meses.push({
      value: hoy.format("YYYY-MM"),
      label: `${hoy.format("MMMM YYYY")} (Actual)`,
    })

    // 6 meses próximos
    for (let i = 1; i <= 6; i++) {
      const mes = hoy.add(i, "month")
      meses.push({
        value: mes.format("YYYY-MM"),
        label: mes.format("MMMM YYYY"),
      })
    }

    return meses
  }

  const mesesOpciones = generarOpcionesMeses()

  // Configuración del calendario
  const calendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: "dayGridMonth",
    locale: esLocale,
    firstDay: 0,
    events: eventos,
    headerToolbar: {
      start: "today,prev,next",
      center: "title",
      end: "dayGridMonth",
    },
    eventClick: handleEventClick,
    dateClick: handleDateClick,
    height: "auto",
    eventDisplay: "block",
    dayMaxEvents: 3,
    eventContent: renderEventContent,
    eventOrder: "start",
    eventTimeFormat: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    dayMaxEventRows: true,
    moreLinkClick: "popover",
    views: {
      dayGridMonth: {
        dayMaxEventRows: 3,
      },
    },
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "0 30px 30px 30px",
          }}
        >
          <div style={{ fontSize: "25px" }}>
            <b>Consulta de Asistencias</b>
          </div>
        </div>

        <Box className="row">
          {/* Sidebar de Estadísticas */}
          <div className="col-xxl-3 col-xl-4">
            <div className="card">
              <div className="card-body p-3">
                <div className="border-bottom pb-4 mb-4">
                  <h5 className="mb-3">
                    Estadísticas {fechaSeleccionada && `- ${dayjs(fechaSeleccionada).format("DD/MM/YYYY")}`}
                  </h5>

                  {fechaSeleccionada && (
                    <Button fullWidth variant="outlined" onClick={resetFiltroFecha} sx={{ mb: 2 }}>
                      Ver todas las fechas
                    </Button>
                  )}

                  {eventosError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Error al cargar estadísticas
                    </Alert>
                  )}

                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1">Total Eventos:</Typography>
                      <Chip label={estadisticas.totalEventos} color="primary" size="small" />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1">Completados:</Typography>
                      <Chip
                        label={estadisticas.completados}
                        sx={{
                          bgcolor: "#4CAF50",
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                        size="small"
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1">En trabajo:</Typography>
                      <Chip
                        label={estadisticas.enTrabajo}
                        sx={{
                          bgcolor: "#2196F3",
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                        size="small"
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1">Pendientes:</Typography>
                      <Chip
                        label={estadisticas.pendientes}
                        sx={{
                          bgcolor: "#FF9800",
                          color: "#000",
                          fontWeight: "bold",
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>
                </div>

                <div>
                  <h6 className="mb-3">Leyenda</h6>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <div style={{ width: 12, height: 12, backgroundColor: "#4CAF50", borderRadius: 2 }} />
                      <Typography variant="caption">Completado</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <div style={{ width: 12, height: 12, backgroundColor: "#2196F3", borderRadius: 2 }} />
                      <Typography variant="caption">En trabajo</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <div style={{ width: 12, height: 12, backgroundColor: "#FF9800", borderRadius: 2 }} />
                      <Typography variant="caption">Pendiente</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <div style={{ width: 12, height: 12, backgroundColor: "#9C27B0", borderRadius: 2 }} />
                      <Typography variant="caption">Horas extras o día libre</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <div style={{ width: 12, height: 12, backgroundColor: "#F44336", borderRadius: 2 }} />
                      <Typography variant="caption">No marcado</Typography>
                    </Box>
                  </Box>
                </div>
              </div>
            </div>
          </div>

          {/* Calendario Principal */}
          <div className="col-xxl-9 col-xl-8">
            {/* Filtros */}
            <CustomFieldsetAccordion title="Filtros" expanded={expanded} onToggle={handleToggle}>
              <Box display="flex" gap={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Mes</InputLabel>
                  <Select name="mes" value={filtros.mes} onChange={handleFiltroChange} label="Mes">
                    {mesesOpciones.map((mes) => (
                      <MenuItem key={mes.value} value={mes.value}>
                        {mes.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {esAdministrador && (
                  <TextField
                    size="small"
                    name="emcodemp"
                    label="Código Empleado"
                    value={filtros.emcodemp}
                    onChange={handleFiltroChange}
                    sx={{ minWidth: 200 }}
                  />
                )}

                <Button
                  variant="contained"
                  onClick={aplicarFiltros}
                  disabled={eventosLoading}
                  startIcon={eventosLoading ? <CircularProgress size={20} /> : null}
                >
                  {eventosLoading ? "Cargando..." : "Aplicar"}
                </Button>

                <Button variant="outlined" onClick={resetFiltros} disabled={eventosLoading}>
                  Limpiar
                </Button>
              </Box>
            </CustomFieldsetAccordion>
            <div className="card border-0">
              <div className="card-body" style={{ padding: "10px", minHeight: "600px" }}>
                {eventosLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography>Cargando eventos...</Typography>
                  </Box>
                ) : eventosError ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4} flexDirection="column">
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Error al cargar eventos
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={() => fetchEventosCalendario(filtros)}
                      startIcon={<span>↻</span>}
                    >
                      Reintentar
                    </Button>
                  </Box>
                ) : eventos.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4} flexDirection="column">
                    <Typography color="textSecondary" sx={{ mb: 2 }}>
                      No hay eventos programados para {dayjs(filtros.mes).format("MMMM YYYY")}
                      {filtros.emcodemp && ` para el empleado ${filtros.emcodemp}`}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => fetchEventosCalendario(filtros)}
                      startIcon={<span>↻</span>}
                    >
                      Recargar
                    </Button>
                  </Box>
                ) : (
                  <FullCalendar {...calendarOptions} />
                )}
              </div>
            </div>
          </div>
        </Box>

        {/* Modal Detalles de Asistencia */}
        <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Detalles de Asistencia
            {detallesLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </DialogTitle>
          <DialogContent>
            {detallesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error al cargar detalles
              </Alert>
            )}

            {detallesAsistencia?.data ? (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Información del Evento */}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: getEstadoColor(detallesAsistencia.data.asistencia?.estado),
                      }}
                    >
                      {detallesAsistencia.data.evento?.empleado_nombre?.charAt(0) || "?"}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {detallesAsistencia.data.evento?.empleado_nombre || "Sin nombre"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {detallesAsistencia.data.evento?.localidad_nombre || "Sin localidad"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {detallesAsistencia.data.evento?.descripcion || "Sin descripción"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Estado */}
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      Estado:
                    </Typography>
                    <Chip
                      label={detallesAsistencia.data.asistencia?.estado || "NO_MARCADO"}
                      size="small"
                      sx={{
                        backgroundColor: getEstadoColor(detallesAsistencia.data.asistencia?.estado),
                        color: "#fff",
                        fontWeight: "bold",
                        ml: 1,
                      }}
                    />
                  </Box>
                </Grid>

                {/* Fecha y Horario */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Fecha:
                  </Typography>
                  {detallesAsistencia.data.evento?.planfecha
                    ? `${dayjs(detallesAsistencia.data.evento.planfecha).format("dddd, D MMMM YYYY")} (${detallesAsistencia.data.evento.planfecha})`
                    : "N/A"}
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Código Horario Planificado:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {detallesAsistencia.data.evento?.horcodigo}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Descripcion Horario Planificado:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {detallesAsistencia.data.evento?.descripcion}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Horario Planificado:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {detallesAsistencia.data.evento?.hora_inicio_planificada || "N/A"} -{" "}
                    {detallesAsistencia.data.evento?.hora_fin_planificada || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Día inicio:
                  </Typography>
                  <Typography variant="body1">
                    {detallesAsistencia.data.evento?.hordiaini?.nombre || "N/A"}(
                    {detallesAsistencia.data.evento?.hordiaini?.codigo || "N/A"})
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Día fin:
                  </Typography>
                  <Typography variant="body1">
                    {detallesAsistencia.data.evento?.hordiafin?.nombre || "N/A"}(
                    {detallesAsistencia.data.evento?.hordiafin?.codigo || "N/A"})
                  </Typography>
                </Grid>

                {/* Entrada y Salida */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Entrada:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {detallesAsistencia.data.asistencia?.entrada?.hora || "No registrada"}
                  </Typography>
                  {detallesAsistencia.data.asistencia?.entrada?.fecha_hora && (
                    <Typography variant="caption" color="textSecondary">
                      {dayjs(detallesAsistencia.data.asistencia.entrada.fecha_hora).format("dddd, D MMMM YYYY")}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Salida:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {detallesAsistencia.data.asistencia?.salida?.hora || "No registrada"}
                  </Typography>
                  {detallesAsistencia.data.asistencia?.salida?.fecha_hora && (
                    <Typography variant="caption" color="textSecondary">
                      {dayjs(detallesAsistencia.data.asistencia.salida.fecha_hora).format("dddd, D MMMM YYYY")}
                    </Typography>
                  )}
                </Grid>

                {/* Tiempo trabajado y horas extras */}
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Tiempo Trabajado:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatTiempoTrabajado(detallesAsistencia.data.asistencia?.tiempo_trabajado)}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Horas Extras:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="secondary">
                    {detallesAsistencia.data.asistencia?.horas_extras || 0}h
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Horas Lunch:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="secondary">
                    {detallesAsistencia.data.asistencia?.tiempo_lunch || 0}h
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Total Marcas:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {detallesAsistencia.data.asistencia?.total_marcas || 0}
                  </Typography>
                </Grid>

                {/* Observaciones */}
                {detallesAsistencia.data.asistencia?.observaciones?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Observaciones:
                    </Typography>
                    <Box sx={{ p: 1.5, bgcolor: "background.default", borderRadius: 1 }}>
                      {detallesAsistencia.data.asistencia.observaciones.map((obs, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                          • {obs}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Detalle de marcas */}
                {detallesAsistencia.data.detalle_marcas?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Detalle de Marcas ({detallesAsistencia.data.detalle_marcas.length}):
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {detallesAsistencia.data.detalle_marcas.map((marca, index) => (
                        <Box
                          key={index}
                          sx={{
                            p: 1,
                            mb: 1,
                            bgcolor:
                              index === 0
                                ? "#E3F2FD"
                                : index === detallesAsistencia.data.detalle_marcas.length - 1
                                  ? "#F3E5F5"
                                  : "transparent",
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography variant="caption" display="block">
                            <strong>Marca {index + 1}:</strong> {marca.fecha_hora_completa}
                          </Typography>
                          <Typography variant="caption" display="block">
                            ID: {marca.ejecnumero}
                          </Typography>
                          {marca.geoposicion && (
                            <Typography variant="caption" display="block">
                              Ubicación: {marca.geoposicion}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : detallesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Cargando detalles...</Typography>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography color="textSecondary">Selecciona un evento para ver los detalles</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailsModal(false)} variant="contained">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default ConsultaDeAsistencia
